/**
 * @file useDeliveryAgreementActions.ts
 * @description Необратимые действия заявки F30 в блоке «Доставка»: письмо
 * клиенту и отмена заявки (phase19.24 шаг 5.2, вынесено из шага 5.1).
 *
 * Оба действия необратимы для клиента — повторное письмо гасит прежнюю
 * ссылку, отмена гасит заявку целиком, — поэтому оба идут через одно окно
 * подтверждения. Хук держит это окно и занятость: у блока остаётся раскладка.
 *
 * Просьба извне (`sendRequest`) приходит с ключом, а не булевым флагом:
 * состояние окна ВЫЧИСЛЯЕТСЯ из запроса, и повторный клик по кнопке полосы
 * «Сейчас» открывает окно снова (находка ревью 5.1).
 */

import { useCallback, useState } from "react";

import {
  cancelDeliveryAgreement,
  deliveryAgreementErrorText,
  sendDeliveryAgreement,
} from "../../../lib/api-delivery-agreement";
import { useNotify } from "../../ui/NotifyProvider";
import type { AgreementConfirmKind } from "../DeliveryAgreementConfirmDialog";

/** Просьба полосы «Сейчас» открыть окно отправки. */
export interface AgreementSendRequest {
  key: number;
  data: AgreementConfirmKind;
}

export interface DeliveryAgreementActions {
  /** Какое окно открыто; `null` — закрыто. */
  openKind: AgreementConfirmKind | null;
  busy: boolean;
  open: (kind: AgreementConfirmKind) => void;
  close: () => void;
  run: (kind: AgreementConfirmKind) => Promise<void>;
}

export function useDeliveryAgreementActions(
  orderId: string,
  onChanged: () => void | Promise<void>,
  sendRequest?: AgreementSendRequest | null,
  onSendHandled?: () => void,
): DeliveryAgreementActions {
  const notify = useNotify();
  const [confirm, setConfirm] = useState<AgreementConfirmKind | null>(null);
  const [busy, setBusy] = useState(false);
  /** Запрос, окно которого менеджер уже закрыл; новый клик даёт новый ключ. */
  const [dismissedKey, setDismissedKey] = useState<number | null>(null);

  const openKind: AgreementConfirmKind | null =
    confirm ?? (sendRequest && sendRequest.key !== dismissedKey ? sendRequest.data : null);

  const close = useCallback(() => {
    setConfirm(null);
    if (sendRequest) setDismissedKey(sendRequest.key);
    onSendHandled?.();
  }, [onSendHandled, sendRequest]);

  const run = useCallback(
    async (kind: AgreementConfirmKind) => {
      setBusy(true);
      try {
        if (kind === "send") {
          await sendDeliveryAgreement(orderId);
          notify.success("Письмо с условиями отправлено клиенту");
        } else {
          await cancelDeliveryAgreement(orderId);
          notify.success("Заявка отменена — условия можно выставить заново");
        }
        close();
        await onChanged();
      } catch (err) {
        notify.error(
          deliveryAgreementErrorText(
            err,
            kind === "send"
              ? "Не удалось отправить предложение"
              : "Не удалось отменить заявку",
          ),
        );
      } finally {
        setBusy(false);
      }
    },
    [close, notify, onChanged, orderId],
  );

  return { openKind, busy, open: setConfirm, close, run };
}
